import { MenuItem } from 'src/app/layout/models/layout-models';

/**
 * Confined roles: a role listed here sees ONLY the routes its scope allows, whatever else the menu
 * and route `roles` would grant. This map is the single place that says which pages a confined role
 * may open and where it lands; RoleScopeGuard enforces it on navigation and filterMenuByScope on the
 * sidenav (and the Settings menu editor), so the two can never disagree.
 *
 * Role names are the NORMALIZED ones (jwt-to-auth.ts lowercases and strips a leading `role_`), hence
 * `trainee` for ROLE_TRAINEE and the bare `intelligence-viewer` authority as-is.
 *
 * The confinement is frontend-only by design: the backend only adds `intelligence-viewer` to the
 * read-only catalog @PreAuthorize lists. The role is for temporary, supervised event accounts that
 * are deleted when the event ends (spec 2026-10-06, decision D7).
 */
export interface RoleScope {
  /** Where a confined user lands when it asks for a route outside its scope. */
  home: string;
  /** Path patterns (query string, matrix params and trailing slash already stripped). */
  allow: RegExp[];
}

export const TRAINEE_ROLE = 'trainee';
export const INTELLIGENCE_VIEWER_ROLE = 'intelligence-viewer';

export const ROLE_SCOPES: { [role: string]: RoleScope } = {
  // NOTE: no '/app/ingest/**' entry. The whole Intelligence section -- including the read-only
  // Printings/Sealed catalog -- is closed to trainees: its route roles (CATALOG_ROLES in
  // ingest-routing) omit `trainee`, the sidenav therefore hides the section, and a trainee who
  // types the URL lands on `home`. The stock table's printing-analysis deep-link is hidden from
  // trainees for the same reason.
  [TRAINEE_ROLE]: {
    home: '/app/warehouse/stock',
    allow: [
      /^\/app\/warehouse\/stock$/,
      /^\/app\/warehouse\/workingMovements$/,
      /^\/app\/shop\/dashboard$/,
      /^\/app\/shop\/cart\/(?!add$)[^/]+$/,
    ],
  },
  // Event role: Intelligence > Printings, Sealed and Scan only, detail overlays, variant sub-routes
  // and every Scan screen included.
  [INTELLIGENCE_VIEWER_ROLE]: {
    home: '/app/ingest/printings',
    allow: [
      /^\/app\/ingest\/printings(\/.*)?$/,
      /^\/app\/ingest\/sealed(\/.*)?$/,
      /^\/app\/ingest\/scan(\/.*)?$/,
    ],
  },
};

/** The scopes of every confined role the user holds, in ROLE_SCOPES order. Empty = unconfined. */
export function scopesFor(roles: readonly string[] | null | undefined): RoleScope[] {
  const held = new Set(roles ?? []);
  return Object.keys(ROLE_SCOPES).filter(r => held.has(r)).map(r => ROLE_SCOPES[r]);
}

export function normalizePath(url: string): string {
  return url.split('?')[0].split('#')[0].split(';')[0].replace(/\/+$/, '');
}

/** A user holding several confined roles may open the union of their scopes. */
export function isAllowedByScopes(scopes: RoleScope[], url: string): boolean {
  if (scopes.length === 0) {
    return true;
  }
  const path = normalizePath(url);
  return scopes.some(s => s.allow.some(re => re.test(path)));
}

/**
 * Drops every menu entry whose route the user's scopes do not allow. A section survives if its own
 * route is allowed or any child survives; a section whose route is outside the scope but which keeps
 * children is kept (its own route is only the click target of a non-selectable header). Unconfined
 * users get the menu back unchanged. Never mutates the input.
 */
export function filterMenuByScope(menu: MenuItem[], roles: readonly string[] | null | undefined): MenuItem[] {
  const scopes = scopesFor(roles);
  if (scopes.length === 0) {
    return menu;
  }
  const allowed = (item: MenuItem) => !!item.route && isAllowedByScopes(scopes, item.route);
  const out: MenuItem[] = [];
  for (const item of menu) {
    if (item.children && item.children.length > 0) {
      const kept = item.children.filter(allowed);
      if (kept.length > 0) {
        out.push({ ...item, children: kept });
      }
      continue;
    }
    if (allowed(item)) {
      out.push({ ...item });
    }
  }
  return out;
}
